import { buildListUrl } from '@util/listings';
import { getCompiledAddress } from '@util/string/';

//Utilities
import { convertSquareFtToSquareM, formatNumberToStringWithCommas } from '@util/listings';
import { M2_TO_SQ_FT } from '@util/constants';

export const getSqFt = (listingData) => {
  const propertySubType = listingData.PropertySubType?.toLowerCase() || null;

  const showBuildingAreaTotal =
    propertySubType !== 'apartment' &&
    propertySubType !== 'condominium' &&
    propertySubType !== 'stock cooperative';

  const livingAreaSqFt =
    listingData.LivingAreaUnits && listingData.LivingAreaUnits === 'Square Meters'
      ? Number(listingData.LivingArea) * M2_TO_SQ_FT
      : listingData.LivingArea;

  if (listingData.OriginatingSystemName === 'TREB' && listingData.Custom5) {
    return listingData.Custom5;
  } else if (listingData.LivingArea) {
    return livingAreaSqFt;
  } else if (propertySubType && listingData.BuildingAreaTotal && showBuildingAreaTotal) {
    return listingData.BuildingAreaTotal;
  } else if (listingData.LotSizeSquareFeet) {
    return listingData.LotSizeSquareFeet;
  } else {
    return null;
  }
};

// Map results for PropertyListingCardGrid component
export const mapResults = (results) => {
  if (!results) return [];

  return results
    .map((r) => {
      // Return null if r doesn't exist, then filter out later
      if (!r) {
        return null;
      }

      const compiledAddress = getCompiledAddress(r); // unparsed address is preferred, this is a fallback

      // ?d=s (small), ?d=l (large), ?d=t (thumbnail)
      const mediaData =
        (r.evaMetadata?.UseEvaMedia || !r.Media?.length) && r.evaMetadata?.Media?.length
          ? { mediaArr: r.evaMetadata?.Media, imageSize: '' }
          : { mediaArr: r.Media, imageSize: '?d=s' };

      const imgs = mediaData?.mediaArr
        ?.filter((media) => media.MediaCategory === 'Photo')
        .map((media) => `${media.MediaURL}${mediaData?.imageSize}`);

      const sqFt = getSqFt(r);

      const squareFeetOrMeters =
        sqFt &&
        (r.squareMeterPreference
          ? `${convertSquareFtToSquareM(sqFt)} Sq M`
          : `${formatNumberToStringWithCommas(sqFt)} Sq Ft`);

      const listUrl = buildListUrl({
        StreetNumber: r.StreetNumber,
        StreetName: r.StreetName,
        City: r.City,
        StateOrProvince: r.StateOrProvince,
        ZipCode: r.PostalCode,
        OriginatingSystemName: r.OriginatingSystemName,
        ListingId: r.ListingId,
      });

      let listPriceMapped;
      if (r.evaMetadata?.IsDevelopment) {
        listPriceMapped = null; // should update with price range when data for developments is available
      } else {
        listPriceMapped = r.ListPrice || 0;
      }

      let openHouse: any[] = [];

      if (r?.OriginatingSystemName === 'EVAMLS') {
        openHouse = r.evaMetadata?.OpenHouse ?? [];
      } else if (r?.evaMetadata?.OpenHouse?.length > 0) {
        if (r?.OpenHouse?.length > 0) {
          openHouse = [...r.evaMetadata?.OpenHouse, ...r?.OpenHouse];
        } else {
          openHouse = r.evaMetadata?.OpenHouse;
        }
      } else {
        openHouse = r.OpenHouse ?? [];
      }

      return {
        address: compiledAddress || r.UnparsedAddress,
        imgs: imgs,
        listPrice: listPriceMapped,
        closePrice: r.ClosePrice || 0,
        closePriceToDisplay: r.ClosePriceToDisplay,
        listUrl,
        openHouse: openHouse || [],
        listingStatus: r.StandardStatus,
        propertySubType: r.PropertySubType,
        propertyType: r.PropertyType,
        bathrooms: r.BathroomsTotalInteger || r.RawMlsBathroomsTotal,
        bedrooms: r.BedroomsTotal,
        squareFootage: squareFeetOrMeters,
        acreage: r.LotSizeAcres || null,
        listOfficeName: r.ListOfficeName || r.evaMetadata.ListOfficeName,
        isFavorite: r.isFavorite || false,
        favId: r.favId,
        lat: r.evaMetadata?.Coordinates?.Lat || r.Latitude || r.RawMlsLatitude,
        Latitude: r.evaMetadata?.Coordinates?.Lat || r.Latitude || r.RawMlsLatitude,
        lng: r.evaMetadata?.Coordinates?.Lon || r.Longitude || r.RawMlsLongitude,
        Longitude: r.evaMetadata?.Coordinates?.Lon || r.Longitude || r.RawMlsLongitude,
        OnMarketDate: r.OnMarketDate,
        Custom14: r.Custom14,
        Custom15: r.Custom15,
        originatingSystemName: r.OriginatingSystemName,
        currencyCode: r.evaMetadata?.Currency || r.CurrencyCode,
        listingId: r.ListingId,
        ListingId: r.ListingId,
        savePropertyTimestamp: r.savePropertyTimestamp,
        country: r.Country,
        standardStatus: r.StandardStatus,
        listAgentFullName: r.ListAgentFullName,
        listAgentEmail: r.ListAgentEmail,
        buyerAgentFullName: r.BuyerAgentFullName,
        buyerOfficeName: r.BuyerOfficeName,
        attributionContact: r.AttributionContact,
        isCooperativeAgency: r.evaMetadata?.CooperativeAgencyYN || false,
        cooperativeAgencyName: r.evaMetadata?.CooperativeAgencyName || '',
        evaId: r.evaMetadata?.EvaId,
      };
    })
    .filter(Boolean); // To remove null values from the result
};
